<script setup lang="ts">
  import type {
    ICatalogMenuEntry,
    TCatalogPlanBadge,
  } from '@/domain/catalog/catalog-menus';

  /**
   * The entries of a ⋯ menu (plan §5.8): 44 px rows with an icon, red for
   * destructive entries, the plan an entry needs as a badge, and write
   * entries disabled while offline.
   */
  defineProps<{
    entries: readonly ICatalogMenuEntry<string>[];
    online: boolean;
  }>();

  const emit = defineEmits<{ (e: 'select', key: string): void }>();

  // Brand names: the same in every language (as in the sidebar).
  const PLAN_BADGE_TEXT: Record<TCatalogPlanBadge, string> = {
    plus: 'Plus',
    pro: 'Pro',
  };
</script>

<template>
  <div class="v2-catalog-action-list">
    <button
      v-for="entry in entries"
      :key="entry.key"
      type="button"
      class="action-entry"
      :class="{ 'is-danger': entry.danger }"
      :disabled="entry.write && !online"
      :title="
        entry.write && !online ? $t('menuPage.errors.offline') : undefined
      "
      @click="emit('select', entry.key)"
    >
      <i class="mdi entry-icon" :class="`mdi-${entry.icon}`" />
      <span class="entry-label">{{ $t(entry.labelKey) }}</span>
      <span v-if="entry.plan" class="entry-plan">{{
        PLAN_BADGE_TEXT[entry.plan]
      }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-catalog-action-list {
    @apply -tw-mx-1.5 -tw-my-1.5 tw-flex tw-flex-col;

    .action-entry {
      @apply tw-flex tw-min-h-[44px] tw-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-left tw-text-sm tw-font-medium tw-text-neutral-800 tw-transition-colors tw-duration-150;

      &:hover:not(:disabled) {
        @apply tw-bg-neutral-100;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }

      &:disabled {
        @apply tw-cursor-default tw-text-neutral-400;
      }

      &.is-danger:not(:disabled) {
        @apply tw-text-red-600;

        &:hover {
          @apply tw-bg-red-50;
        }

        .entry-icon {
          @apply tw-text-red-500;
        }
      }

      .entry-icon {
        @apply tw-mr-3 tw-flex-none tw-text-xl tw-leading-none tw-text-neutral-500;
      }

      .entry-label {
        @apply tw-min-w-0 tw-flex-1 tw-py-2 tw-leading-snug;
      }

      .entry-plan {
        @apply tw-ml-2 tw-flex-none tw-rounded-full tw-bg-neutral-100 tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-uppercase tw-leading-none tw-tracking-wide tw-text-neutral-600;
      }
    }
  }
</style>
